/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from './api';

const name = 'email';

const postEmailTemplate = createAsyncThunk(
  'email/postEmailTemplate',
  async (payload, { rejectWithValue }) => {
    try {
      return await api.postEmailTemplate(payload);
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);
const postCustomEmailTemplate = createAsyncThunk(
  'email/postCustomEmailTemplate',
  async (payload, { rejectWithValue }) => {
    try {
      return await api.postCustomEmailTemplate(payload);
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);
const getEmailDesignOptions = createAsyncThunk(
  'email/getEmailDesignOptions',
  async (_, { rejectWithValue }) => {
    try {
      return await api.getEmailDesignOptions();
    } catch (err) {
      if (!err.response) throw err;
      return rejectWithValue(err.response.data);
    }
  }
);
const previewEmailTemplate = createAsyncThunk(
  'email/previewEmailTemplate',
  async (payload, { rejectWithValue }) => {
    try {
      return await api.previewEmailTemplate(payload);
    } catch (err) {
      if (!err.response) throw err;
      return rejectWithValue(err.response.data);
    }
  }
);
const previewCustomEmailTemplate = createAsyncThunk(
  'email/previewCustomEmailTemplate',
  async (payload, { rejectWithValue }) => {
    try {
      return await api.previewCustomEmailTemplate(payload);
    } catch (err) {
      if (!err.response) throw err;
      return rejectWithValue(err.response.data);
    }
  }
);
const postCustomBlock = createAsyncThunk(
  'email/postCustomBlock',
  async (block, { rejectWithValue }) => {
    try {
      const response = await api.postCustomBlock({
        ...(block.id ? { id: block.id } : {}),
        name: block.name,
        description: block.description,
        content: block.html,
        status: block.status ?? 0,
      });
      return { ...block, ...response, html: response?.content ?? block.html };
    } catch (err) {
      if (!err.response) throw err;
      return rejectWithValue(err.response.data);
    }
  }
);
const getCustomBlocks = createAsyncThunk(
  'email/getCustomBlocks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getCustomBlocks();
      const blocks = Array.isArray(response) ? response : response?.data || [];
      return blocks.map((block) => ({
        ...block,
        html: block.content || '',
      }));
    } catch (err) {
      if (!err.response) throw err;
      return rejectWithValue(err.response.data);
    }
  }
);
const testCustomEmailTemplate = createAsyncThunk(
  'email/testCustomEmailTemplate',
  async (payload, { rejectWithValue }) => {
    try {
      return await api.testCustomEmailTemplate(payload);
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);
const testEmailTemplate = createAsyncThunk(
  'email/testEmailTemplate',
  async (payload, { rejectWithValue }) => {
    try {
      return await api.testEmailTemplate(payload);
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

const getEmailTemplates = createAsyncThunk(
  'email/getEmailTemplates',
  async (payload, { rejectWithValue }) => {
    try {
      return await api.getEmailTemplates(payload);
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);
const getCustomEmailTemplates = createAsyncThunk(
  'email/getCustomEmailTemplates',
  async (payload, { rejectWithValue }) => {
    try {
      return await api.getCustomEmailTemplates(payload);
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);
const getCustomEmailVariables = createAsyncThunk(
  'email/getCustomEmailVariables',
  async (payload, { rejectWithValue }) => {
    try {
      return await api.getCustomEmailVariables(payload);
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

const { actions, reducer } = {
  ...createSlice({
    name,
    initialState: {
      isLoading: false,
      error: {},
      emailTemplatesList: { data: [], total: 0 },
      customEmailTemplatesList: { data: [], total: 0 },
      customBlocks: [],
      variables: [],
    },
    reducers: {
      setIsLoggedIn: (state, action) => ({
        ...state,
        isLoggedIn: action.payload,
      }),
      setUser: (state, action) => ({
        ...state,
        user: action.payload,
      }),
      setCustomBlocks: (state, action) => {
        state.customBlocks = action.payload;
      },
    },
    extraReducers: {
      [postEmailTemplate.pending]: (state) => {
        state.isLoading = true;
      },
      [postEmailTemplate.fulfilled]: (state) => {
        state.isLoading = false;
      },
      [postEmailTemplate.rejected]: (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      },
      [getEmailTemplates.pending]: (state) => {
        state.isLoading = true;
      },
      [getEmailTemplates.fulfilled]: (state, action) => {
        state.isLoading = false;
        state.emailTemplatesList = action.payload;
      },
      [getEmailTemplates.rejected]: (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      },
      [getCustomEmailTemplates.pending]: (state) => {
        state.isLoading = true;
      },
      [getCustomEmailTemplates.fulfilled]: (state, action) => {
        state.isLoading = false;
        state.customEmailTemplatesList = action.payload;
      },
      [getCustomEmailTemplates.rejected]: (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      },
      [getCustomEmailVariables.pending]: (state) => {
        state.isLoading = true;
      },
      [getCustomEmailVariables.fulfilled]: (state, action) => {
        state.isLoading = false;
        state.variables = action.payload;
      },
      [getCustomEmailVariables.rejected]: (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      },
      [getCustomBlocks.fulfilled]: (state, action) => {
        state.customBlocks = action.payload;
      },
      [getEmailDesignOptions.fulfilled]: (state, action) => {
        state.designOptions = action.payload;
      },
    },
  }),
};

const selectors = {
  selectEmailTemplates: (state) =>
    state[name].emailTemplatesList || { data: [], total: 0 },
  selectCustomEmailTemplates: (state) =>
    state[name].customEmailTemplatesList || { data: [], total: 0 },
  selectVariables: (state) => state[name].variables,
  selectCustomBlocks: (state) => state[name].customBlocks || [],
  selectEmailDesignOptions: (state) => state[name].designOptions || null,
};

export default {
  actions: {
    ...actions,
    postEmailTemplate,
    postCustomEmailTemplate,
    postCustomBlock,
    getCustomBlocks,
    getEmailDesignOptions,
    previewEmailTemplate,
    previewCustomEmailTemplate,
    testEmailTemplate,
    testCustomEmailTemplate,
    getEmailTemplates,
    getCustomEmailTemplates,
    getCustomEmailVariables,
  },
  selectors,
  reducer,
  name,
};
